/* eslint-disable no-type-assertion/no-type-assertion -- backend payloads are typed loosely; we cast at the boundary after validating shape. */

import { type Message } from '@copilotkit/shared'

import { parseUserMessageText } from 'modules/ai/chat/protocol/userMessageTags'
import { extractVisionAttachments } from 'modules/ai/chat/protocol/visionAttachments'

/** Shape returned by `GET /copilotkit/chat-history/threads/:threadId/messages`. */
export type BackendMessage = {
  id: string
  role: 'user' | 'assistant'
  content:
    | string
    | {
        content?: string
        parts?: BackendPart[]
      }
  createdAt?: string
}

type BackendTextPart = { type: 'text'; text: string }
type BackendFilePart = {
  type: 'binary'
  mimeType: string
  filename?: string
  url?: string
}
type LegacyBackendFilePart = {
  type: 'file'
  file: {
    url: string
    filename: string
    mimetype: string
  }
}
type BackendToolPart = {
  type: 'tool-invocation'
  toolInvocation: {
    toolCallId: string
    toolName: string
    args: Record<string, unknown>
    state?: string
    result?: unknown
  }
}
type BackendPart = BackendTextPart | BackendFilePart | LegacyBackendFilePart | BackendToolPart

type MessageTextPart = { type: 'text'; text: string }
type MessageBinaryPart = {
  type: 'binary'
  url: string
  filename: string
  mimeType: string
}

type ActionWithRender = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render?: (...args: any[]) => any
}

// Backend collapses each turn into one message with an ordered `parts`
// array; the live flow uses one message per tool call. Re-expand so
// each tool renders as its own widget in the correct slot.
export function convertBackendMessage(
  backendMsg: BackendMessage,
  actions?: Record<string, ActionWithRender>
): Message[] {
  const { id, role, content } = backendMsg

  if (typeof content === 'string') {
    if (role === 'user') {
      const { text, replyQuote } = parseUserMessageText(content)
      return [
        {
          id,
          role,
          content: text,
          ...(replyQuote ? { replyQuote } : {}),
        } as Message,
      ]
    }
    return [{ id, role, content }]
  }

  const parts = content.parts
  if (!parts || parts.length === 0) {
    return [{ id, role, content: content.content ?? '' }]
  }

  const messages: Message[] = []
  let partIndex = 0
  const userParts: Array<MessageTextPart | MessageBinaryPart> = []
  let userReplyQuote: string | undefined

  for (const part of parts) {
    if (part.type === 'text') {
      if (part.text) {
        if (role === 'user') {
          // Backend wraps image attachments in `<vision_attachments>` blocks;
          // split them out so the UI receives the same shape as the live flow.
          const { text, binaryParts } = extractVisionAttachments(part.text)
          for (const bp of binaryParts) {
            userParts.push(bp)
          }
          if (text.length > 0) {
            const parsed = parseUserMessageText(text)
            if (parsed.replyQuote && !userReplyQuote) {
              userReplyQuote = parsed.replyQuote
            }
            if (parsed.text.length > 0) {
              userParts.push({ type: 'text', text: parsed.text })
            }
          }
        } else {
          messages.push({
            id: `${id}_t${partIndex}`,
            role,
            content: part.text,
          })
        }
      }
    } else if (part.type === 'binary') {
      if (role === 'user' && part.url && part.filename && part.mimeType) {
        userParts.push({
          type: 'binary',
          url: part.url,
          filename: part.filename,
          mimeType: part.mimeType,
        })
      }
    } else if (part.type === 'file') {
      if (role === 'user' && part.file) {
        userParts.push({
          type: 'binary',
          url: part.file.url,
          filename: part.file.filename,
          mimeType: part.file.mimetype,
        })
      }
    } else {
      const { toolInvocation: inv } = part
      const toolCall = {
        id: inv.toolCallId,
        type: 'function' as const,
        function: {
          name: inv.toolName,
          arguments: JSON.stringify(inv.args),
        },
      }

      const msg: Message = {
        id: `${id}_tc${partIndex}`,
        role,
        content: '',
        toolCalls: [toolCall],
      }

      // `generativeUI` only lives on `AIMessage` but CopilotKit reads it
      // off the union at runtime; the cast bypasses the TS gap.
      const action = actions?.[inv.toolName]
      if (action?.render) {
        const renderFn = action.render
        const args = inv.args
        const result = inv.result
        const toolCallId = inv.toolCallId
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(msg as any).generativeUI = (props?: Record<string, unknown>) =>
          renderFn({
            status: 'complete',
            args,
            result,
            toolCallId,
            respond: () => {},
            ...props,
          })
      }

      messages.push(msg)
    }
    partIndex++
  }

  if (role === 'user' && userParts.length > 0) {
    messages.push({
      id,
      role,
      content: userParts,
      ...(userReplyQuote ? { replyQuote: userReplyQuote } : {}),
    } as Message)
  }

  return messages.length > 0 ? messages : [{ id, role, content: content.content ?? '' }]
}

/**
 * @param actions Registered CopilotKit actions (from `useCopilotContext`).
 *   When provided, tool invocations are wired to their render functions.
 */
export async function fetchThreadMessages(
  baseUrl: string,
  headers: Record<string, string>,
  threadId: string,
  actions?: Record<string, ActionWithRender>,
  runtimeFetch: typeof fetch = fetch
): Promise<Message[]> {
  const response = await runtimeFetch(`${baseUrl}/chat-history/threads/${threadId}/messages`, {
    credentials: 'include',
    headers: { ...headers },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch thread messages: ${response.status}`)
  }

  const data = (await response.json()) as { messages: BackendMessage[] }
  return data.messages.flatMap((msg) => convertBackendMessage(msg, actions))
}
